import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { authorizedRequest } from '../../api/authorizedRequest';

interface EmployeeDto {
  ssn: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

interface EmployeeCheckDto {
  checkNumber: string;
  date: string;
  endDate: string;
  grossPay: number;
  netPay: number;
  sequence: string;
}

interface DirectDepositDto {
  description: string;
  amount: number;
  ytd: number;
}

interface EarningDto {
  description: string;
  units: number;
  amount: number;
  ytd: number;
}

interface GetTaxOutputDto {
  description: string;
  amount: number;
  ytd: number;
}

interface GetOtherDeductionOutputDto {
  description: string;
  amount: number;
  ytd: number;
}

interface GetEarningsDetailOutputDto {
  payId: string;
  rate: number;
  units: number;
  job: string;
  jcCostCode: string;
  amount: number;
  periodEndDate: string;
  workDate: string;
  payType: string;
  jobName: string;
}

interface CheckData {
  check: EmployeeCheckDto;
  directDeposits: DirectDepositDto[];
  earnings: EarningDto[];
  earningsDetail: GetEarningsDetailOutputDto[];
  taxes: GetTaxOutputDto[];
  otherDeductions: GetOtherDeductionOutputDto[];
}

const tableHeaderStyle = {
  style: {
    backgroundColor: '#343a40',
    color: 'white',
  },
};
const tableCellStyle = {
  style: {
    color: 'white',
    fontWeight: 'bold' as const,
  },
};
const customTableStyles = {
  headRow: tableHeaderStyle,
  headCells: tableCellStyle,
};

const directDepositColumns = [
  { name: 'Account Information', selector: (row: DirectDepositDto) => row.description, grow: 3 },
  { name: 'This Check', selector: (row: DirectDepositDto) => row.amount.toFixed(2) },
  { name: 'YTD', selector: (row: DirectDepositDto) => row.ytd.toFixed(2) },
];

const earningsColumns = [
  { name: 'Description', selector: (row: EarningDto) => row.description, grow: 3 },
  { name: 'Hours/Units', selector: (row: EarningDto) => row.units },
  { name: 'This Check', selector: (row: EarningDto) => row.amount.toFixed(2) },
  { name: 'YTD', selector: (row: EarningDto) => row.ytd.toFixed(2) },
];

const earningsDetailColumns = [
  { name: 'PayId', selector: (row: GetEarningsDetailOutputDto) => row.payId },
  { name: 'Work Date', selector: (row: GetEarningsDetailOutputDto) => row.workDate ? new Date(row.workDate).toLocaleDateString() : '' },
  { name: 'Hours/Units', selector: (row: GetEarningsDetailOutputDto) => row.units },
  { name: 'Rate', selector: (row: GetEarningsDetailOutputDto) => row.rate.toFixed(2) },
  { name: 'Amount', selector: (row: GetEarningsDetailOutputDto) => row.amount.toFixed(2) },
  { name: 'Job', selector: (row: GetEarningsDetailOutputDto) => row.job },
  { name: 'Cost Code', selector: (row: GetEarningsDetailOutputDto) => row.jcCostCode },
];

const taxesColumns = [
  { name: 'Taxes', selector: (row: GetTaxOutputDto) => row.description, grow: 3 },
  { name: 'This Check', selector: (row: GetTaxOutputDto) => row.amount.toFixed(2) },
  { name: 'YTD', selector: (row: GetTaxOutputDto) => row.ytd.toFixed(2) },
];

const otherDeductionsColumns = [
  { name: 'Description', selector: (row: GetOtherDeductionOutputDto) => row.description, grow: 3 },
  { name: 'This Check', selector: (row: GetOtherDeductionOutputDto) => row.amount.toFixed(2) },
  { name: 'YTD', selector: (row: GetOtherDeductionOutputDto) => row.ytd.toFixed(2) },
];

const EmployeeChecksSummary: React.FC = () => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [employee, setEmployee] = useState<EmployeeDto | null>(null);
  const [checksData, setChecksData] = useState<CheckData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    authorizedRequest({ url: '/Employee/GetCurrentUser', method: 'GET' })
      .then(res => setEmployee(res.data))
      .catch(() => setError('Unable to load employee details.'));
  }, []);

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }
    setError('');
    setIsLoading(true);
    setHasSearched(true);
    setChecksData([]);

    try {
      const checksRes = await authorizedRequest({
        url: '/Employee/Checks',
        method: 'GET',
        params: {
          StartDate: startDate,
          EndDate: endDate,
          MaxResultCount: 1000,
          SkipCount: 0,
          Sorting: 'Date asc',
        },
      });

      const checks: EmployeeCheckDto[] = checksRes.data.items ?? [];

      if (checks.length === 0) {
        setChecksData([]);
        setIsLoading(false);
        return;
      }

      const checkDataArray = await Promise.all(
        checks.map(async (check) => {
          const cd = check.date.toString().substring(0, 10);
          const ed = check.endDate.toString().substring(0, 10);
          const seq = check.sequence;

          const [ddRes, earnRes, taxesRes, otherDedRes, earnDetailRes] = await Promise.all([
            authorizedRequest({ url: `/Employee/DirectDeposits?checkDate=${cd}&endDate=${ed}&sequence=${seq}`, method: 'GET' }),
            authorizedRequest({ url: `/Employee/Earnings?checkDate=${cd}&endDate=${ed}&sequence=${seq}`, method: 'GET' }),
            authorizedRequest({ url: `/Employee/Taxes?checkDate=${cd}&endDate=${ed}&sequence=${seq}`, method: 'GET' }),
            authorizedRequest({ url: `/Employee/OtherDeduction?checkDate=${cd}&endDate=${ed}&sequence=${seq}`, method: 'GET' }),
            authorizedRequest({ url: `/Employee/EarningsDetail?checkDate=${cd}&endDate=${ed}&sequence=${seq}`, method: 'GET' }),
          ]);

          return {
            check,
            directDeposits: ddRes.data.items ?? [],
            earnings: earnRes.data.items ?? [],
            taxes: taxesRes.data.items ?? [],
            otherDeductions: otherDedRes.data.items ?? [],
            earningsDetail: earnDetailRes.data.items ?? [],
          } as CheckData;
        })
      );

      setChecksData(checkDataArray);
    } catch (err) {
      console.error('Error fetching summary data:', err);
      setError('An error occurred while fetching data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('printdiv');
    if (printContent) {
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  };

  return (
    <div className="pd-ltr-20">
      {/* Page Header */}
      <div className="card-box pd-20 height-100-p mb-30">
        <div className="row align-items-center">
          <div className="col-md-12">
            <h4 className="font-20 weight-500 mb-10 text-capitalize">
              Employee Checks Summary
            </h4>
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-3 mb-2">
              <label className="form-label font-weight-bold">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 mb-2">
              <label className="form-label font-weight-bold">End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 mb-2 d-flex align-items-end gap-2">
              <button
                className="btn btn-secondary mr-2"
                type="button"
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  'Generate'
                )}
              </button>
              {checksData.length > 0 && (
                <button className="btn btn-outline-secondary" type="button" onClick={handlePrint}>
                  <i className="fa fa-print mr-1"></i> Print
                </button>
              )}
            </div>
          </div>
          {error && (
            <div className="alert alert-danger mt-2 mb-0">{error}</div>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '30vh' }}>
          <div className="spinner-border text-primary" role="status" style={{ width: '4rem', height: '4rem' }}></div>
          <div className="mt-3">Fetching check details. Please wait...</div>
        </div>
      )}

      {/* No results */}
      {!isLoading && hasSearched && checksData.length === 0 && (
        <div className="alert alert-info">No checks found for the selected date range.</div>
      )}

      {/* Results */}
      {!isLoading && checksData.length > 0 && (
        <>
          {/* Employee Info */}
          {employee && (
            <div className="card border-dark mb-4">
              <div className="card-body text-dark">
                <h5 className="card-title">Employee Details</h5>
                <p className="card-text"><b>SSN: </b>XXX-XX-{employee.ssn}</p>
                <p className="card-text">{employee.name}</p>
                <p className="card-text">{employee.address1}</p>
                {employee.address2 && <p className="card-text">{employee.address2}</p>}
                <p className="card-text">{employee.city}, {employee.state} {employee.zip}</p>
              </div>
            </div>
          )}

          {/* One section per check */}
          {checksData.map((item, idx) => (
            <div key={idx} className="mb-5">
              {/* Check Header */}
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>Check #{item.check.checkNumber}</strong>
                  {' — '}
                  Check Date: {new Date(item.check.date).toLocaleDateString()}
                  {' | '}
                  Period End: {new Date(item.check.endDate).toLocaleDateString()}
                  {' | '}
                  Gross Pay: <strong>${item.check.grossPay.toFixed(2)}</strong>
                  {' | '}
                  Net Pay: <strong>${item.check.netPay.toFixed(2)}</strong>
                </div>
              </div>

              {/* Direct Deposits */}
              <div className="text-center mt-3">
                <h6 className="card-header d-flex justify-content-between align-items-center">
                  Direct Deposits
                </h6>
                <div className="card-body p-0">
                  <DataTable
                    columns={directDepositColumns}
                    data={item.directDeposits}
                    customStyles={customTableStyles}
                    dense
                    noDataComponent={<div className="p-2 text-muted">No direct deposits</div>}
                  />
                </div>
              </div>

              {/* Earnings */}
              <div className="text-center mt-3">
                <h6 className="card-header d-flex justify-content-between align-items-center">
                  Earnings
                </h6>
                <div className="card-body p-0">
                  <DataTable
                    columns={earningsColumns}
                    data={item.earnings}
                    customStyles={customTableStyles}
                    dense
                    noDataComponent={<div className="p-2 text-muted">No earnings</div>}
                  />
                </div>
              </div>

              {/* Earnings Details */}
              <div className="text-center mt-3">
                <h6 className="card-header d-flex justify-content-between align-items-center">
                  Earnings Details
                </h6>
                <div className="card-body p-0">
                  <DataTable
                    columns={earningsDetailColumns}
                    data={item.earningsDetail}
                    customStyles={customTableStyles}
                    dense
                    noDataComponent={<div className="p-2 text-muted">No earnings details</div>}
                  />
                </div>
              </div>

              {/* Taxes */}
              <div className="text-center mt-3">
                <h6 className="card-header d-flex justify-content-between align-items-center">
                  Taxes
                </h6>
                <div className="card-body p-0">
                  <DataTable
                    columns={taxesColumns}
                    data={item.taxes}
                    customStyles={customTableStyles}
                    dense
                    noDataComponent={<div className="p-2 text-muted">No taxes</div>}
                  />
                </div>
              </div>

              {/* Other Deductions */}
              <div className="text-center mt-3">
                <h6 className="card-header d-flex justify-content-between align-items-center">
                  Other Deductions
                </h6>
                <div className="card-body p-0">
                  <DataTable
                    columns={otherDeductionsColumns}
                    data={item.otherDeductions}
                    customStyles={customTableStyles}
                    dense
                    noDataComponent={<div className="p-2 text-muted">No other deductions</div>}
                  />
                </div>
              </div>

              <hr className="mt-4" />
            </div>
          ))}
        </>
      )}

      {/* ── Printable content ── */}
      <div style={{ display: 'none' }}>
        <div id="printdiv">
          {employee && (
            <>
              <h2 style={{ textAlign: 'center' }}>Site Development, Inc.</h2>
              <h3 style={{ textAlign: 'center' }}>Employee Checks Summary</h3>
              {startDate && endDate && (
                <p style={{ textAlign: 'center' }}>
                  Period: {new Date(startDate).toLocaleDateString()} – {new Date(endDate).toLocaleDateString()}
                </p>
              )}
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td style={{ width: '15%' }}>Name:</td>
                    <td style={{ width: '35%' }}>{employee.name}</td>
                    <td style={{ width: '15%' }}>Address:</td>
                    <td style={{ width: '35%' }}>{employee.address1}, {employee.city}, {employee.state} {employee.zip}</td>
                  </tr>
                  <tr>
                    <td>SSN:</td>
                    <td>XXX-XX-{employee.ssn}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {checksData.map((item, idx) => (
            <div key={idx} style={{ pageBreakInside: 'avoid', marginBottom: '2rem' }}>
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td style={{ width: '20%' }}>Check Number:</td>
                    <td style={{ width: '30%' }}>{item.check.checkNumber}</td>
                    <td style={{ width: '20%' }}>Check Date:</td>
                    <td style={{ width: '30%' }}>{new Date(item.check.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}</td>
                  </tr>
                  <tr>
                    <td>Period End:</td>
                    <td>{new Date(item.check.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}</td>
                    <td>Gross Pay:</td>
                    <td>{item.check.grossPay.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Net Pay:</td>
                    <td>{item.check.netPay.toFixed(2)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>

              {/* Direct Deposits */}
              <table className="table table-sm">
                <thead>
                  <tr><th colSpan={3} style={{ textAlign: 'center' }}>Direct Deposits</th></tr>
                  <tr><th style={{ width: '60%' }}>Account Information</th><th>This Check</th><th>YTD</th></tr>
                </thead>
                <tbody>
                  {item.directDeposits.map((r, i) => (
                    <tr key={i}><td>{r.description}</td><td>{r.amount.toFixed(2)}</td><td>{r.ytd.toFixed(2)}</td></tr>
                  ))}
                  {item.directDeposits.length === 0 && <tr><td colSpan={3}>No direct deposits</td></tr>}
                </tbody>
              </table>

              {/* Earnings */}
              <table className="table table-sm">
                <thead>
                  <tr><th colSpan={4} style={{ textAlign: 'center' }}>Earnings</th></tr>
                  <tr><th style={{ width: '50%' }}>Description</th><th>Hours/Units</th><th>This Check</th><th>YTD</th></tr>
                </thead>
                <tbody>
                  {item.earnings.map((r, i) => (
                    <tr key={i}><td>{r.description}</td><td>{r.units}</td><td>{r.amount.toFixed(2)}</td><td>{r.ytd.toFixed(2)}</td></tr>
                  ))}
                  {item.earnings.length === 0 && <tr><td colSpan={4}>No earnings</td></tr>}
                </tbody>
              </table>

              {/* Earnings Details */}
              <table className="table table-sm">
                <thead>
                  <tr><th colSpan={7} style={{ textAlign: 'center' }}>Earnings Details</th></tr>
                  <tr><th>PayId</th><th>Work Date</th><th>Hours/Units</th><th>Rate</th><th>Amount</th><th>Job</th><th>Cost Code</th></tr>
                </thead>
                <tbody>
                  {item.earningsDetail.map((r, i) => (
                    <tr key={i}>
                      <td>{r.payId}</td>
                      <td>{r.workDate ? new Date(r.workDate).toLocaleDateString() : ''}</td>
                      <td>{r.units}</td>
                      <td>{r.rate.toFixed(2)}</td>
                      <td>{r.amount.toFixed(2)}</td>
                      <td>{r.job}</td>
                      <td>{r.jcCostCode}</td>
                    </tr>
                  ))}
                  {item.earningsDetail.length === 0 && <tr><td colSpan={7}>No earnings details</td></tr>}
                </tbody>
              </table>

              {/* Taxes */}
              <table className="table table-sm">
                <thead>
                  <tr><th colSpan={3} style={{ textAlign: 'center' }}>Taxes</th></tr>
                  <tr><th style={{ width: '60%' }}>Description</th><th>This Check</th><th>YTD</th></tr>
                </thead>
                <tbody>
                  {item.taxes.map((r, i) => (
                    <tr key={i}><td>{r.description}</td><td>{r.amount.toFixed(2)}</td><td>{r.ytd.toFixed(2)}</td></tr>
                  ))}
                  {item.taxes.length === 0 && <tr><td colSpan={3}>No taxes</td></tr>}
                </tbody>
              </table>

              {/* Other Deductions */}
              <table className="table table-sm">
                <thead>
                  <tr><th colSpan={3} style={{ textAlign: 'center' }}>Other Deductions</th></tr>
                  <tr><th style={{ width: '60%' }}>Description</th><th>This Check</th><th>YTD</th></tr>
                </thead>
                <tbody>
                  {item.otherDeductions.map((r, i) => (
                    <tr key={i}><td>{r.description}</td><td>{r.amount.toFixed(2)}</td><td>{r.ytd.toFixed(2)}</td></tr>
                  ))}
                  {item.otherDeductions.length === 0 && <tr><td colSpan={3}>No other deductions</td></tr>}
                </tbody>
              </table>

              {idx < checksData.length - 1 && <hr style={{ pageBreakAfter: 'always' }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmployeeChecksSummary;
